/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * chunkedUpload — Feb 6, 2026 · hardened pause/resume Feb 7, 2026 ·
 * Adaptive parallelism + structured trace logging Feb 8, 2026.
 *
 * Resumable / chunked upload helper. Splits a File into 25 MiB pieces,
 * PUTs each one to the backend chunked-upload service, then calls a
 * purpose-specific completion endpoint.
 *
 * Resilience contract (P0, locked):
 *   • Per-chunk retry (6 attempts) with exponential backoff + jitter
 *     (cap 15s).
 *   • If retries are exhausted on a chunk:
 *       1) the helper PROBES `/status` — server may already have it.
 *       2) if still missing → throws `UploadPausedError` carrying
 *          {uploadId, lastSuccessfulChunk, totalChunks, originalError,
 *          failedChunkIndex, lastHttpStatus, lastErrorDetail}.
 *          The server-side session (received_chunks, parts on disk)
 *          survives untouched for 24 h, so the caller can pause UI,
 *          show a "Resume upload" CTA, and re-invoke chunkedUpload
 *          with `resumeUploadId` to pick up exactly where it stopped.
 *   • If access cookie expires mid-upload, identical contract via
 *     `AuthExpiredError` (refresh attempted first; no progress lost).
 *   • Upload NEVER silently freezes — onProgress is called with
 *     stage="paused" before any thrown pause/auth error.
 *   • Structured `[upload-trace]` console events on every chunk
 *     attempt + result so a user reproducing on a phone can paste
 *     console output and the operator can see exactly which chunk
 *     failed with which HTTP status / detail.
 *
 * Adaptive parallelism (Feb 8, 2026):
 *   Defaults to 3 parallel workers. On the FIRST chunk that retries
 *   ≥ 3 times OR returns a deterministic 400 ("must be exactly N
 *   bytes" — iOS Safari truncating a 25 MiB body in flight), we
 *   self-throttle to 1 parallel worker for the rest of the upload.
 *   This kills the deterministic 75 MiB-in-flight failure mode on
 *   slow/iOS cellular connections without sacrificing speed for
 *   healthy uploads.
 *
 *   await chunkedUpload({
 *     file,                         // browser File (omit if resumeUploadId)
 *     purpose: "clip_basket",       // or "lesson_review"
 *     context: { basket_id: "..." },
 *     completeUrl: `/api/clip-basket/${basket_id}/chunked-complete`,
 *     completeBody: { upload_id, order: 0 },   // upload_id is injected
 *     onProgress: ({pct, sent, total, stage, upload_id, ...debug}) => ...,
 *     signal: AbortSignal,
 *     resumeUploadId: "abc123",     // OPTIONAL — skips init, seeds
 *                                   //   received_chunks from server.
 *   });
 *
 * Owner constraint: "Do not break current AI review or clip basket
 * flow." This helper produces a clip_doc / review_doc with the same
 * downstream shape — the backend reassembles into the canonical
 * location and the existing pipelines are untouched.
 */
import axios from "axios";
import { isIOSSafari } from "./iosDetect";
import { shouldAutoRetry, RETRY_POLICY } from "./uploadRecovery";

const API = process.env.REACT_APP_BACKEND_URL;
// Per-chunk retry policy is OWNED by uploadRecovery.js (the recovery
// authority — see PLATFORM_REBUILD_ARCHITECTURE.md §4 + CANONICAL §6).
// chunkedUpload READS the policy here so the values can never drift in
// two places. Operator-locked: 4 attempts at 1.5/3/6/12 s.
const MAX_RETRIES_PER_CHUNK = RETRY_POLICY.max_attempts;
const BACKOFF_CAP_MS = 15000;
const PARALLEL_CHUNKS_DEFAULT = 3;
// iPhone-Safe Upload Profile (May 22, 2026):
//   • Suggest 5 MiB chunks to /init on iOS Safari (server clamps).
//   • Bump per-chunk XHR timeout from 90s → 180s on iOS so a slow
//     cellular link doesn't hang a 5 MiB chunk before retry kicks in.
const IOS_CLIENT_CHUNK_SIZE_HINT = 5 * 1024 * 1024;
const IOS_PER_CHUNK_TIMEOUT_MS = 180000;
const DEFAULT_PER_CHUNK_TIMEOUT_MS = 90000;

// Structured trace event — mirrors what the in-app debug panel reads.
// We always log to console.info; the consumer can turn the in-app
// panel on/off via `localStorage.lr_debug_uploads = "true"`.
function _trace(event, payload) {
  if (typeof console === "undefined" || !console.info) return;
  try {
    console.info(`[upload-trace] ${event}`, payload);
  } catch { /* noop — never let logging crash the upload */ }
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * AuthExpiredError — thrown when the session expired DURING an upload
 * and even a refresh attempt failed. The server-side upload session
 * (init row in `db.chunked_uploads`) survives untouched, so the caller
 * can prompt the user to re-authenticate and call
 * `chunkedUpload({ ...same args, resumeUploadId: err.uploadId })`
 * to pick up exactly where the user left off — no upload progress
 * is discarded.
 */
export class AuthExpiredError extends Error {
  constructor(uploadId, currentChunk, totalChunks) {
    super("Session expired during upload");
    this.name = "AuthExpiredError";
    this.uploadId = uploadId;
    this.currentChunk = currentChunk;
    this.totalChunks = totalChunks;
  }
}

/**
 * UploadPausedError — thrown when a chunk's retries (3 attempts +
 * one server-status probe) all fail. The upload is NOT dead: the
 * session lives on the server for 24 h, every successfully-received
 * chunk is preserved, and the caller can resume by calling
 * `chunkedUpload({ ...same args, resumeUploadId: err.uploadId })`.
 *
 *   - `uploadId`              — server-side session id
 *   - `lastSuccessfulChunk`   — index of the last chunk we know
 *                               landed (`-1` if none).
 *   - `totalChunks`           — total expected chunks for this file
 *   - `originalError`         — the underlying axios / network error
 */
export class UploadPausedError extends Error {
  constructor(uploadId, lastSuccessfulChunk, totalChunks, originalError, extras = {}) {
    super("Upload paused — connection issue. Tap to resume.");
    this.name = "UploadPausedError";
    this.uploadId = uploadId;
    this.lastSuccessfulChunk = lastSuccessfulChunk;
    this.totalChunks = totalChunks;
    this.originalError = originalError;
    // Diagnostic — surfaced in the in-app debug panel + caller's
    // toast. Always populated when available so the user/operator
    // can see the actual HTTP status + server detail of the blocking
    // failure (this was the deterministic loop pain-point).
    this.failedChunkIndex = extras.failedChunkIndex ?? null;
    this.lastHttpStatus = extras.lastHttpStatus ?? null;
    this.lastErrorDetail = extras.lastErrorDetail ?? null;
    this.totalAttempts = extras.totalAttempts ?? null;
  }
}

/**
 * DiskExhaustedError — thrown when the BACKEND host filesystem cannot
 * fit the upload. Backend returns HTTP 507 with a structured detail
 * envelope: `{error: "host_storage_exhausted", stage, message, ...}`.
 *
 * This is NOT a retryable condition — the disk does not magically grow.
 * Callers must surface a clear message and stop. Recovery requires
 * either operator action (more disk) or the user choosing a different
 * environment (production has more storage than preview).
 *
 * Carries:
 *   - `stage`               — `"init"` or `"chunk_write"`
 *   - `uploadId`            — if `stage === "chunk_write"`
 *   - `availableBytes`      — host free bytes at the time of failure
 *   - `requiredBytes`       — bytes the request needed
 *   - `serverMessage`       — server's pre-formatted operator-friendly
 *                             message; safe to display verbatim
 */
export class DiskExhaustedError extends Error {
  constructor(detail = {}) {
    super(detail.message || "Preview storage is full — upload cannot continue here. Try production or use a shorter clip.");
    this.name = "DiskExhaustedError";
    this.stage = detail.stage || null;
    this.uploadId = detail.upload_id || null;
    this.chunkIndex = detail.chunk_index ?? null;
    this.availableBytes = detail.available_bytes ?? null;
    this.requiredBytes = detail.required_bytes ?? null;
    this.serverMessage = detail.message || null;
  }
}

async function tryRefreshSession() {
  try {
    await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
    return true;
  } catch {
    return false;
  }
}

/**
 * Upload `file` in chunks. Resolves with the JSON response from the
 * purpose's completion endpoint (e.g. the basket summary).
 *
 * If `resumeUploadId` is provided we SKIP `/init`, fetch `/status` to
 * see which chunks the server already has, and only upload the rest.
 * `file` MUST still be the same File object the user originally
 * picked — chunk indexes and byte ranges depend on it.
 */
export async function chunkedUpload({
  file,
  purpose,
  context = {},
  completeUrl,
  completeBody = {},
  onProgress,
  signal,
  resumeUploadId,
  parallelChunks,   // optional override — caller can pin to 1 on iOS
  onLifecycleEvent, // optional — diagnostic-only observability hook
}) {
  if (!file) throw new Error("No file");
  if (!purpose) throw new Error("Missing purpose");
  if (!completeUrl) throw new Error("Missing completeUrl");

  // Observability shim. Diagnostic-only: never throws, never blocks,
  // never gates behaviour. Defaults to no-op for callers that don't
  // pass a sink (preserves backwards-compat for non-instrumented
  // mount points). The sink receives canonical event names from
  // CANONICAL_UPLOAD_ARCHITECTURE.md §7 — no free-form names.
  const _emit = (name, payload) => {
    if (typeof onLifecycleEvent !== "function") return;
    try { onLifecycleEvent(name, payload || {}); }
    catch { /* never let observability crash the upload */ }
  };

  // Detect Cloudflare-shaped 5xx (empty body, no `detail` field).
  // Returns true when an error looks like a CF edge response rather
  // than a structured backend error. Used in failure payloads.
  const _cloudflareShaped = (err) => {
    const status = err?.response?.status;
    if (typeof status !== "number") return false;
    if (status < 500 || status > 599) return false;
    const body = err?.response?.data;
    if (body == null) return true;
    if (typeof body === "string" && body.trim() === "") return true;
    if (typeof body === "object" && !body.detail) return true;
    return false;
  };

  // iPhone-Safe Upload Profile defaults — applied automatically
  // when the helper detects iOS Safari, even if the caller didn't
  // override `parallelChunks`. This is a defence layer: if a new
  // caller ever forgets the iOS settings, the helper still does the
  // right thing on a real iPhone.
  const _isIOS = isIOSSafari();
  const _perChunkTimeoutMs = _isIOS
    ? IOS_PER_CHUNK_TIMEOUT_MS
    : DEFAULT_PER_CHUNK_TIMEOUT_MS;
  const _clientChunkSizeHint = _isIOS ? IOS_CLIENT_CHUNK_SIZE_HINT : null;

  // ── 1. Init session (or rehydrate via /status if resuming) ──────
  let upload_id;
  let chunk_size;
  let total_chunks;
  let received = new Set();
  let sentBytes = 0;

  if (resumeUploadId) {
    // Resume path — fetch the existing session from the server.
    const _statusUrl = `${API}/api/uploads/chunked/${resumeUploadId}/status`;
    _emit("upload_resume_attempted", {
      upload_id: resumeUploadId,
      endpoint_url: _statusUrl,
      method: "GET",
      attempted_at: new Date().toISOString(),
      file_name: file.name,
      file_size: file.size,
      file_type: file.type || null,
    });
    let statusRes;
    try {
      statusRes = await axios.get(
        _statusUrl,
        { withCredentials: true, signal },
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      const status = err?.response?.status;
      _emit("upload_resume_attempted", {
        upload_id: resumeUploadId,
        endpoint_url: _statusUrl,
        method: "GET",
        result: "failed",
        http_status: status ?? null,
        cloudflare_shaped: _cloudflareShaped(err),
        error_message: err?.message || null,
        error_detail_field: err?.response?.data?.detail || null,
        failure_phase: "during_resume_probe",
        attempted_at: new Date().toISOString(),
      });
      throw new Error(`Resume failed: ${detail || "couldn't reach upload session"}`);
    }
    const s = statusRes.data || {};
    upload_id = resumeUploadId;
    chunk_size = s.chunk_size;
    total_chunks = s.total_chunks;
    for (const i of s.received_chunks || []) received.add(i);
    // Recompute sentBytes precisely from the file slices we already
    // know landed — keeps the progress bar honest on resume.
    for (const i of received) {
      const start = i * chunk_size;
      const end = Math.min(start + chunk_size, file.size);
      sentBytes += Math.max(0, end - start);
    }
    if (typeof console !== "undefined" && console.info) {
      const nextIdx = (() => {
        for (let n = 0; n < total_chunks; n++) if (!received.has(n)) return n;
        return total_chunks;
      })();
      const seedPct = file.size
        ? Math.round((sentBytes / file.size) * 100)
        : 0;
      console.info(
        `[chunkedUpload] resuming uploadId=${upload_id} ` +
        `receivedChunks=${received.size}/${total_chunks} ` +
        `nextChunkIndex=${nextIdx} ` +
        `displayedPercent=${seedPct}%`,
      );
    }
    _emit("upload_init_acked", {
      upload_id,
      resume_used: true,
      chunk_size,
      total_chunks,
      received_chunks_count: received.size,
      bytes_received: sentBytes,
      total_bytes: file.size,
    });
  } else {
    const _initUrl = `${API}/api/uploads/chunked/init`;
    _emit("upload_init_requested", {
      endpoint_url: _initUrl,
      method: "POST",
      file_name: file.name,
      file_size: file.size,
      file_type: file.type || null,
      file_last_modified: file.lastModified || null,
      purpose,
      client_chunk_size_hint: _clientChunkSizeHint,
      resume_used: false,
    });
    let initRes;
    try {
      initRes = await axios.post(
        _initUrl,
        {
          filename: file.name,
          total_bytes: file.size,
          purpose,
          context,
          mime_type: file.type || null,
          // iPhone-Safe Upload Profile — server clamps; null = default.
          client_chunk_size_hint: _clientChunkSizeHint,
        },
        { withCredentials: true, signal },
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      _emit("upload_init_requested", {
        endpoint_url: _initUrl,
        method: "POST",
        result: "failed",
        http_status: err?.response?.status ?? null,
        cloudflare_shaped: _cloudflareShaped(err),
        error_message: err?.message || null,
        error_detail_field: detail || null,
        failure_phase: "before_chunk",
      });
      // Backend preflight may return HTTP 507 with a structured
      // `host_storage_exhausted` envelope. Surface it as a typed
      // error so the caller can show a clear non-retryable message
      // instead of pretending the upload can continue.
      if (
        err?.response?.status === 507
        && detail && typeof detail === "object"
        && detail.error === "host_storage_exhausted"
      ) {
        throw new DiskExhaustedError(detail);
      }
      throw new Error(detail || err?.message || "Init failed");
    }
    upload_id = initRes.data.upload_id;
    chunk_size = initRes.data.chunk_size;
    total_chunks = initRes.data.total_chunks;
    _emit("upload_init_acked", {
      upload_id,
      resume_used: false,
      chunk_size,
      total_chunks,
      total_bytes: file.size,
    });
  }

  // Throttle the onProgress callback so the consumer's React tree
  // doesn't re-render on every byte. The most-recent payload always
  // wins; "stage" changes always flush immediately.
  let _lastProgressAt = 0;
  let _pendingProgressTimer = null;
  let _lastStage = "uploading";
  const PROGRESS_THROTTLE_MS = 500;

  // Speed metrics — sliding window over the last few chunk completions.
  const _uploadStartedAt = Date.now();
  let _retryCount = 0;
  let _lastHttpStatus = null;        // surfaced via onProgress for the debug panel
  let _lastErrorDetail = null;
  let _lastFailedChunk = null;
  let _parallelChunks = (typeof parallelChunks === "number" && parallelChunks > 0)
    ? parallelChunks
    : (_isIOS ? 1 : PARALLEL_CHUNKS_DEFAULT);
  const _failsByChunk = new Map();   // index → consecutive-fail count
  const _completionTimestamps = [];   // [{ at, bytes }]

  function _recordChunkCompletion(bytes) {
    _completionTimestamps.push({ at: Date.now(), bytes });
    // Keep only the last ~30 chunk timings.
    if (_completionTimestamps.length > 30) _completionTimestamps.shift();
  }

  function _currentMbps() {
    if (_completionTimestamps.length < 2) return null;
    const first = _completionTimestamps[0];
    const last = _completionTimestamps[_completionTimestamps.length - 1];
    const dtSec = Math.max(0.001, (last.at - first.at) / 1000);
    const totalBytes = _completionTimestamps.reduce((a, c) => a + c.bytes, 0);
    return (totalBytes * 8) / 1_000_000 / dtSec;   // Mbps
  }

  function _flushProgress(stage) {
    if (typeof onProgress !== "function") return;
    const pct = Math.min(100, Math.round((sentBytes / file.size) * 100));
    let lastSuccessful = -1;
    for (const i of received) if (i > lastSuccessful) lastSuccessful = i;
    onProgress({
      pct,
      sent: sentBytes,
      total: file.size,
      current_chunk: received.size,
      total_chunks,
      last_successful_chunk: lastSuccessful,
      upload_id,
      stage,
      mbps: _currentMbps(),
      // ── Diagnostic surface (Feb 8, 2026) ─────────────────────────
      // Consumed by the optional in-app debug panel
      // (LessonReview.js, gated on localStorage.lr_debug_uploads).
      // Cheap to compute, never expensive — produced once per
      // throttled progress flush.
      retry_count: _retryCount,
      last_http_status: _lastHttpStatus,
      last_error_detail: _lastErrorDetail,
      last_failed_chunk: _lastFailedChunk,
      parallelism: _parallelChunks,
      session_age_seconds: Math.round((Date.now() - _uploadStartedAt) / 1000),
    });
    _lastProgressAt = Date.now();
  }

  const reportProgress = (stage = "uploading") => {
    // Stage transitions ALWAYS flush immediately (preparing →
    // uploading → finalizing → complete; or → paused). Within a
    // stage, throttle to one update per 500 ms.
    const stageChanged = stage !== _lastStage;
    _lastStage = stage;
    if (stageChanged || Date.now() - _lastProgressAt >= PROGRESS_THROTTLE_MS) {
      if (_pendingProgressTimer) {
        clearTimeout(_pendingProgressTimer);
        _pendingProgressTimer = null;
      }
      _flushProgress(stage);
      return;
    }
    if (_pendingProgressTimer) return;
    _pendingProgressTimer = setTimeout(() => {
      _pendingProgressTimer = null;
      _flushProgress(_lastStage);
    }, PROGRESS_THROTTLE_MS);
  };
  reportProgress();

  // ── 2. Upload chunks with retry + adaptive parallelism ─────────
  // Per-chunk upload helper. Returns:
  //   { ok: true }                            - chunk landed
  //   { ok: false, err, lastErr, paused: true }  - retries exhausted
  //   throws AuthExpiredError on hard auth-fail
  //
  // Buffer-first send (Feb 8, 2026): we read the Blob into an
  // ArrayBuffer BEFORE handing it to axios. This sets a hard
  // Content-Length on the request and avoids the iOS Safari mid-flight
  // body-truncation pattern that was causing the deterministic 400
  // "Chunk N must be exactly M bytes" loop on cellular uploads.
  async function uploadOneChunk(i) {
    const start = i * chunk_size;
    const end = Math.min(start + chunk_size, file.size);
    const blob = file.slice(start, end);
    let buffer;
    try {
      buffer = await blob.arrayBuffer();
    } catch (err) {
      _trace("chunk_slice_failed", { chunk: i, error: err?.message });
      return { ok: false, err, paused: true };
    }
    let lastErr = null;
    for (let attempt = 0; attempt < MAX_RETRIES_PER_CHUNK; attempt++) {
      if (signal?.aborted) throw new Error("Upload cancelled");
      _trace("chunk_attempt_started", {
        upload_id, chunk: i, total_chunks,
        attempt: attempt + 1, max_attempts: MAX_RETRIES_PER_CHUNK,
        bytes: buffer.byteLength, parallelism: _parallelChunks,
      });
      try {
        await axios.put(
          `${API}/api/uploads/chunked/${upload_id}/chunk/${i}`,
          buffer,
          {
            withCredentials: true,
            headers: { "Content-Type": "application/octet-stream" },
            signal,
            transformRequest: [(d) => d],
            // Long-tail uploads on slow cellular need a generous
            // timeout — default Axios timeout (none) is OK but make
            // it explicit so a stalled request doesn't hang the
            // worker forever. iPhone-Safe Upload Profile bumps this
            // to 180 s on iOS Safari (5 MiB chunk over a poor
            // cellular link) — desktop stays at 90 s.
            timeout: _perChunkTimeoutMs,
          },
        );
        received.add(i);
        sentBytes += buffer.byteLength;
        _recordChunkCompletion(buffer.byteLength);
        _failsByChunk.delete(i);   // reset fail count on success
        _trace("chunk_success", {
          upload_id, chunk: i, bytes: buffer.byteLength,
          received_count: received.size, total_chunks,
        });
        _emit("upload_chunk_uploaded", {
          upload_id,
          chunk_index: i,
          bytes: buffer.byteLength,
          received_count: received.size,
          total_chunks,
          endpoint_url: `${API}/api/uploads/chunked/${upload_id}/chunk/${i}`,
        });
        reportProgress();
        return { ok: true };
      } catch (err) {
        lastErr = err;
        if (signal?.aborted) throw new Error("Upload cancelled");
        const status = err?.response?.status;
        const detail =
          err?.response?.data?.detail || err?.message || "network error";
        _lastHttpStatus = status ?? null;
        _lastErrorDetail = (typeof detail === "string"
          ? detail
          : JSON.stringify(detail)).slice(0, 240);
        _lastFailedChunk = i;
        _failsByChunk.set(i, (_failsByChunk.get(i) || 0) + 1);

        if (status === 401) {
          _trace("chunk_auth_expired", { upload_id, chunk: i });
          const refreshed = await tryRefreshSession();
          if (refreshed) {
            _trace("chunk_auth_refreshed", { upload_id, chunk: i });
            attempt -= 1;
            continue;
          }
          if (typeof console !== "undefined" && console.warn) {
            console.warn(
              `[chunkedUpload] auth expired on chunk ${i} of ${total_chunks} — pausing.`,
            );
          }
          reportProgress("paused");
          throw new AuthExpiredError(upload_id, i, total_chunks);
        }

        // Adaptive throttle (Feb 8, 2026): a deterministic 400
        // "Chunk N must be exactly M bytes" indicates body truncation
        // upstream. Three parallel 25 MiB requests on iOS cellular are
        // the classic trigger. As soon as we see this signature OR
        // the same chunk fails 3 times in a row, drop the worker pool
        // to single-threaded for the rest of the upload — buys us
        // resilience at the cost of ~3× upload time on bad networks.
        const isDeterministic400 = (
          status === 400
          && typeof detail === "string"
          && detail.includes("must be exactly")
        );
        if (
          _parallelChunks > 1
          && (isDeterministic400 || (_failsByChunk.get(i) || 0) >= 3)
        ) {
          _parallelChunks = 1;
          _trace("parallelism_throttled_to_one", {
            upload_id, chunk: i,
            reason: isDeterministic400 ? "size_mismatch_400" : "consecutive_fails",
            consecutive_fails: _failsByChunk.get(i) || 0,
            http_status: status,
          });
          if (typeof console !== "undefined" && console.warn) {
            console.warn(
              `[chunkedUpload] adaptive throttle — parallel workers reduced from ` +
              `${PARALLEL_CHUNKS_DEFAULT} → 1 after ${isDeterministic400 ? "deterministic 400 size mismatch" : "3+ consecutive chunk failures"} on chunk ${i}. ` +
              `Slower but more resilient on cellular / iOS Safari.`,
            );
          }
        }

        _retryCount += 1;
        if (typeof console !== "undefined" && console.warn) {
          console.warn(
            `[chunkedUpload] chunk ${i}/${total_chunks - 1} attempt ${attempt + 1}/${MAX_RETRIES_PER_CHUNK} failed (status=${status ?? "n/a"}): ${detail}`,
          );
        }
        _trace("chunk_attempt_failed", {
          upload_id, chunk: i, attempt: attempt + 1,
          status, detail: _lastErrorDetail,
          consecutive_fails: _failsByChunk.get(i) || 0,
          parallelism_now: _parallelChunks,
        });
        // Surface the failed-state into the debug panel immediately
        // (no need to wait for the next progress flush).
        reportProgress();
        // Disk-exhaustion short-circuit (May 24, 2026 — option C).
        // HTTP 507 with `host_storage_exhausted` envelope is a TERMINAL
        // host-side condition — the disk will not magically grow on a
        // retry. Throw the typed error immediately so the caller can
        // show a clear "preview storage full" message rather than the
        // retry/pause/UploadPausedError cascade.
        const errDetail = err?.response?.data?.detail;
        if (
          status === 507
          && errDetail && typeof errDetail === "object"
          && errDetail.error === "host_storage_exhausted"
        ) {
          throw new DiskExhaustedError(errDetail);
        }
        // Retry decision routes through the single recovery authority
        // (uploadRecovery.shouldAutoRetry — see PLATFORM_REBUILD §10
        // Phase B). The authority decides whether to retry and how
        // long to wait. Local backoff math is GONE — there can only
        // be one policy.
        const decision = shouldAutoRetry(err, attempt);
        if (!decision.retry) {
          _trace("chunk_retry_terminal", {
            upload_id, chunk: i, attempt: attempt + 1,
            reason: decision.reason, http_status: status,
          });
          _emit("upload_chunk_retried", {
            upload_id,
            chunk_index: i,
            attempt: attempt + 1,
            result: "terminal",
            reason: decision.reason,
            http_status: status ?? null,
            cloudflare_shaped: _cloudflareShaped(err),
            endpoint_url: `${API}/api/uploads/chunked/${upload_id}/chunk/${i}`,
            failure_phase: "during_chunk",
            total_chunks,
          });
          break;
        }
        _trace("chunk_retry_scheduled", {
          upload_id, chunk: i, attempt: attempt + 1,
          delay_ms: decision.delay_ms, attempts_left: decision.attempts_left,
          reason: decision.reason,
        });
        _emit("upload_chunk_retried", {
          upload_id,
          chunk_index: i,
          attempt: attempt + 1,
          result: "scheduled",
          reason: decision.reason,
          delay_ms: decision.delay_ms,
          attempts_left: decision.attempts_left,
          http_status: status ?? null,
          cloudflare_shaped: _cloudflareShaped(err),
          endpoint_url: `${API}/api/uploads/chunked/${upload_id}/chunk/${i}`,
          total_chunks,
        });
        await sleep(Math.min(BACKOFF_CAP_MS, decision.delay_ms));
      }
    }
    // Probe — server may already have it but the response was lost.
    try {
      const probe = await axios.get(
        `${API}/api/uploads/chunked/${upload_id}/status`,
        { withCredentials: true, signal },
      );
      const srvHave = new Set(probe.data?.received_chunks || []);
      if (srvHave.has(i)) {
        received.add(i);
        sentBytes += buffer.byteLength;
        _recordChunkCompletion(buffer.byteLength);
        _failsByChunk.delete(i);
        reportProgress();
        if (typeof console !== "undefined" && console.info) {
          console.info(
            `[chunkedUpload] chunk ${i} was already on server — recovered via /status probe.`,
          );
        }
        _trace("chunk_recovered_via_probe", { upload_id, chunk: i });
        return { ok: true };
      }
    } catch { /* fall through */ }
    return { ok: false, err: lastErr, paused: true };
  }

  // Worker-pool — adaptive concurrency. Starts at PARALLEL_CHUNKS_DEFAULT
  // (3) and self-throttles to 1 on the first signal of upstream
  // truncation. Sequential-equivalent retry semantics: if any worker
  // exhausts retries, we PAUSE the whole upload (other in-flight
  // workers finish or abort cleanly).
  const queue = [];
  for (let i = 0; i < total_chunks; i++) {
    if (!received.has(i)) queue.push(i);
  }
  let pauseRequested = null;   // first error → throw after wind-down

  async function worker(workerSlotIndex) {
    while (queue.length > 0) {
      if (signal?.aborted) throw new Error("Upload cancelled");
      if (pauseRequested) return;
      // Adaptive throttle compliance — workers above the current
      // limit voluntarily exit when a chunk failure has dropped
      // _parallelChunks. Worker 0 always continues so we never go
      // to zero-worker by accident.
      if (workerSlotIndex >= _parallelChunks && workerSlotIndex > 0) return;
      const i = queue.shift();
      if (i === undefined) return;
      const result = await uploadOneChunk(i);
      if (!result.ok && result.paused) {
        pauseRequested = { i, err: result.err };
        return;
      }
    }
  }

  _trace("upload_started", {
    upload_id, total_chunks, file_size: file.size,
    chunk_size, parallelism: _parallelChunks,
    resume: Boolean(resumeUploadId),
    received_at_start: received.size,
  });

  await Promise.all(
    Array.from(
      { length: Math.min(_parallelChunks, queue.length || 1) },
      (_, slotIdx) => worker(slotIdx),
    ),
  );

  if (pauseRequested) {
    let lastSuccessful = -1;
    for (const idx of received) if (idx > lastSuccessful) lastSuccessful = idx;
    if (typeof console !== "undefined" && console.warn) {
      const detail =
        pauseRequested.err?.response?.data?.detail
        || pauseRequested.err?.message
        || "network error";
      console.warn(
        `[chunkedUpload] pausing upload ${upload_id} after exhausting retries on chunk ${pauseRequested.i}/${total_chunks - 1}: ${detail}. ${received.size}/${total_chunks} chunks safe on server.`,
      );
    }
    _trace("upload_paused", {
      upload_id,
      failed_chunk: pauseRequested.i,
      last_successful: lastSuccessful,
      total_chunks,
      received_count: received.size,
      last_http_status: _lastHttpStatus,
      last_error_detail: _lastErrorDetail,
      parallelism_at_pause: _parallelChunks,
      total_retries: _retryCount,
    });
    reportProgress("paused");
    throw new UploadPausedError(
      upload_id,
      lastSuccessful,
      total_chunks,
      pauseRequested.err,
      {
        failedChunkIndex: pauseRequested.i,
        lastHttpStatus: _lastHttpStatus,
        lastErrorDetail: _lastErrorDetail,
        totalAttempts: _retryCount,
      },
    );
  }

  // ── 3. Complete ─────────────────────────────────────────────────
  // Up to 2 attempts: if the server returns a structured
  // `missing_chunks` 409 (self-healed phantoms), re-enter the chunk
  // loop to re-upload them, then retry finalize once.
  for (let finalizeAttempt = 0; finalizeAttempt < 2; finalizeAttempt++) {
    reportProgress("finalizing");
    const _completeUrl = `${API}${completeUrl}`;
    _emit("upload_complete_requested", {
      upload_id,
      endpoint_url: _completeUrl,
      method: "POST",
      finalize_attempt: finalizeAttempt,
      total_chunks,
      received_count: received.size,
    });
    let completeRes;
    try {
      completeRes = await axios.post(
        _completeUrl,
        { ...completeBody, upload_id },
        { withCredentials: true, signal },
      );
      reportProgress("complete");
      _emit("upload_complete_acked", {
        upload_id,
        endpoint_url: _completeUrl,
        finalize_attempt: finalizeAttempt,
        asset_size: file.size,
      });
      return completeRes.data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Structured "missing_chunks" — server self-healed phantoms.
      // Re-upload exactly those chunks then retry finalize once.
      if (
        err?.response?.status === 409
        && detail
        && typeof detail === "object"
        && detail.error === "missing_chunks"
        && Array.isArray(detail.missing)
        && finalizeAttempt === 0
      ) {
        if (typeof console !== "undefined" && console.warn) {
          console.warn(
            `[chunkedUpload] server reported missing_chunks on finalize — re-uploading ${detail.missing.length} chunk(s): ${detail.missing.slice(0, 10).join(", ")}${detail.missing.length > 10 ? "…" : ""}`,
          );
        }
        // Strike them from our local `received` set so the loop below
        // re-uploads them.
        for (const idx of detail.missing) {
          received.delete(idx);
          // Refund the bytes we previously counted as sent.
          const start = idx * chunk_size;
          const end = Math.min(start + chunk_size, file.size);
          sentBytes -= Math.max(0, end - start);
        }
        if (sentBytes < 0) sentBytes = 0;
        reportProgress("uploading");

        // Re-enter the chunk loop ONLY for the missing indexes — use
        // the shared `uploadOneChunk` helper so retry/probe/auth
        // semantics match the main path.
        for (const i of detail.missing) {
          if (signal?.aborted) throw new Error("Upload cancelled");
          const result = await uploadOneChunk(i);
          if (!result.ok && result.paused) {
            let lastSuccessful = -1;
            for (const idx of received) if (idx > lastSuccessful) lastSuccessful = idx;
            reportProgress("paused");
            throw new UploadPausedError(
              upload_id,
              lastSuccessful,
              total_chunks,
              result.err,
            );
          }
        }
        // Loop iterates → retries finalize.
        continue;
      }
      // Any other error — surface it.
      const text = (typeof detail === "string" && detail)
        || detail?.message
        || err?.message;
      _emit("upload_integrity_failed", {
        upload_id,
        endpoint_url: `${API}${completeUrl}`,
        method: "POST",
        finalize_attempt: finalizeAttempt,
        http_status: err?.response?.status ?? null,
        cloudflare_shaped: _cloudflareShaped(err),
        error_message: err?.message || null,
        error_detail_field: detail || null,
        failure_phase: "during_complete",
      });
      throw new Error(`Finalization failed: ${text || "server error"}`);
    }
  }
  // Defensive — shouldn't reach here.
  throw new Error("Finalization failed: max retries exceeded");
}

/** Convenience: friendly file-size formatter for progress UIs. */
export function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v.toFixed(v >= 100 || u === 0 ? 0 : 1)} ${units[u]}`;
}
